import './OrderPage.scss';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Fade, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { includes } from 'lodash';
import { asOrderPageData } from './core/types';
import { useOrderPage } from './orderPageContext/useOrderPage';
import { useLanguage } from '../utils/GeneralUtils';
import MonkeybookWidgetFooter from '../MonkeybookWidgetFooter';
import ErrorBox from './steps/ErrorBox';
import { OrderPageTypography } from './themedComponents/OrderPageTypography';
import { OrderPageError } from './themedComponents/OrderPageError';
import { SpecificEventSummary, StyledImage } from './steps/SpecificEventSummary';
import { OrderPageIntent } from './OrderPageIntent';
import ConfirmationDialog from './components/ConfimationDialog';
import { getPrimaryPhoto } from '../utils/orderPageImageUtils';
import { AccountLogo } from '../shared/ui/AccountLogo';
import { useIntentConfig } from './intentConfig/useIntentConfig';
import { StepId } from './intentConfig/types';
import { OrderPageErrors } from './hooks/OrderPageErrors';
import { useOrderNavigation } from './hooks/useOrderNavigation';
import OrderPageStepper from './components/OrderPageStepper';
import StepRenderer from './components/StepRenderer';
import OrderPageActions from './components/OrderPageActions';
import { HeadlinePrice } from './components/HeadlinePrice';

type OrderPageProps = {
  storefrontMode?: boolean;
};

const OrderPage = ({ storefrontMode }: OrderPageProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { orderPageState } = useOrderPage();
  const formRef = useRef();
  const personalDetailsRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<OrderPageErrors | undefined>(undefined);
  // Set when a save failure has a specific, customer-actionable cause (a weekly slot that just
  // filled up, say). Overrides the generic save-exception copy.
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [shouldDiasbleNext, setShouldDiasbleNext] = useState(false);
  const language = useLanguage();

  const { voucherError, orderPageIntent, orderPage, specificEventError } = orderPageState;
  const specificEvent = !!orderPageState?.specificEvent;
  const orderPageLanguages = orderPageState?.account.customer_facing_widgets_languages;
  const queryParams = new URLSearchParams(window.location.search);
  const embeddedWidget = queryParams.get('embed') === 'true';
  const hideImageAndLogo = storefrontMode || embeddedWidget;
  const hideTitle = !!queryParams.get('hidetitle') || storefrontMode;
  const waitingListUrl = orderPageState.experience
    ? `/waiting-list/${orderPageState.account.id}/${orderPageState.experience.id}/${orderPage.id}/${
        orderPageState.details?.persons || 1
      }`
    : '';
  const waitListEnabled = !!asOrderPageData(orderPageState.orderPage)?.waiting_list?.enabled;

  const { currentStepId, currentStepIndex } = useIntentConfig();
  // Only bookable pages carry images. A pass never reaches the hero anyway: the pass flow always
  // renders <OrderPage storefrontMode />, and storefrontMode hides it.
  const bookableOrderPage = asOrderPageData(orderPageState.orderPage);

  const { handleNext, handleBack, handleReset, nextButtonText, isResetDialogOpen, setIsResetDialogOpen } =
    useOrderNavigation({
      formRef,
      personalDetailsRef,
      embeddedWidget,
      setError,
      setErrorMessage,
      setLoading,
    });

  const [heightNode, setHeightNode] = useState<HTMLElement | null>(null);
  const elementRef = useCallback((node: any) => {
    setHeightNode(node ?? null);
  }, []);

  useEffect(() => {
    if (!heightNode) return undefined;

    const postHeight = () => {
      // An iframe inside a hidden container (a tab the customer has not opened yet) has a
      // zero-sized viewport, so document.body.offsetHeight there is the collapsed loader
      // height. Reporting it would freeze the embedding iframe at the wrong height.
      if (window.innerWidth === 0 || window.innerHeight === 0) return;
      window.parent.postMessage({ height: document.body.offsetHeight }, '*');
    };

    const resizeObserver = new ResizeObserver(postHeight);
    resizeObserver.observe(heightNode);

    // The embedding page can resize our iframe without our own content changing size, in
    // which case the ResizeObserver above never fires again and we would stay clipped.
    // Re-report whenever we become visible so the parent can always recover.
    const intersectionObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) postHeight();
    });
    intersectionObserver.observe(heightNode);

    document.addEventListener('visibilitychange', postHeight);
    postHeight();

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener('visibilitychange', postHeight);
    };
  }, [heightNode]);

  if (voucherError) {
    return (
      <ErrorBox
        account={orderPageState.account}
        orderPageLabel={orderPageState.orderPage.label[language] ?? ''}
        error={voucherError}
      />
    );
  }
  if (specificEventError) {
    return (
      <ErrorBox
        account={orderPageState.account}
        orderPageLabel={orderPageState.orderPage.label[language] ?? ''}
        error={specificEventError}
        date={orderPageState.dateAndTimeDetails?.date ?? undefined}
        waitingListUrl={waitingListUrl}
        waitListEnabled={waitListEnabled}
      />
    );
  }

  const shouldShowStartOverButton =
    currentStepId === StepId.PAYMENT && orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD;
  return (
    <Grid
      container
      display="flex"
      justifyContent="space-around"
      sx={{ '::-webkit-scrollbar': { display: 'none' } }}
      style={{
        height: '100%',
        width: '100%',
      }}
      ref={personalDetailsRef}>
      <Grid
        ref={elementRef}
        container
        flexDirection="column"
        style={{
          borderRadius: '12px',
          background: theme.customTheme.palette.background,
          height: '100%',
          width: '100%',
          maxWidth: '710px',
        }}
        className="order-page-wrapper">
        {orderPageState.account.name === 'Speculo' && (
          <>
            <script async src="https://www.googletagmanager.com/gtag/js?id=UA-148720460-1" />
            <script src="/scripts/speculo-gtag.js" />
            <script src="/scripts/speculo-facebook.js" />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                src="https://www.facebook.com/tr?id=935698493466004&ev=PageView&noscript=1"
                alt="facebook link"
              />
            </noscript>
          </>
        )}
        {orderPageState.account.id === '64e246f85f3e1733728cb75f' && (
          <>
            <script async src="https://www.googletagmanager.com/gtag/js?id=G-SZ6K3X7MKK" />
            <script src="/scripts/yaar-gtag.js" />
            <script src="/scripts/yaar-facebook.js" />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                src="https://www.facebook.com/tr?id=935698493466004&ev=PageView&noscript=1"
                alt="facebook link"
              />
            </noscript>
          </>
        )}

        <Grid
          px={isSmallScreen ? 2 : 4}
          container
          flexDirection="column"
          className={`order-page-box${currentStepIndex !== 0 ? ' order-page-box-padding' : ''}`}
          mx={storefrontMode ? 0 : 'auto'}
          my={storefrontMode ? undefined : 3}
          style={{
            maxWidth: '650px',
            color: theme.customTheme.palette.text.primary,
            border: storefrontMode && !isSmallScreen ? 'none' : `2px solid ${theme.customTheme.palette.border}`,
          }}
          boxShadow={4}>
          {shouldShowStartOverButton && (
            <Box
              sx={{ justifyContent: 'flex-start', display: 'flex' }}
              onClick={() => setIsResetDialogOpen(true)}
              mb={2}>
              <OrderPageTypography
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: 'text.secondary',
                }}>
                {t('reset')}
              </OrderPageTypography>
            </Box>
          )}
          {!hideImageAndLogo && (
            <Grid container justifyContent="center" mb={2}>
              <AccountLogo account={orderPageState.account} width={200} style={{ width: 200 }} alt="Account logo" />
            </Grid>
          )}
          {!hideTitle && (
            <Grid container justifyContent="center">
              <OrderPageTypography variant="h1" style={{ textAlign: 'center', width: '100%' }}>
                {orderPageState.orderPage.label[language]}
              </OrderPageTypography>
            </Grid>
          )}
          <Grid container mt={3} mb={1}>
            <OrderPageStepper />
            <OrderPageError
              isShowingError={includes(
                [
                  OrderPageErrors.SAVE_ORDER_EXCEPTION,
                  OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION,
                  OrderPageErrors.AT_LEAST_ONE_PERSON,
                ],
                error,
              )}
              message={
                errorMessage ||
                (error === OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION
                  ? t('orderPageErrors.saveExceptionNonIsraeliPhoneNumber')
                  : error === OrderPageErrors.AT_LEAST_ONE_PERSON
                  ? t('orderPageErrors.atLeastOnePerson')
                  : t('orderPageErrors.saveException'))
              }
              setClearError={() => {
                setError(undefined);
                setErrorMessage(undefined);
              }}
            />
            {!hideImageAndLogo && currentStepIndex === 0 && (
              <Grid container flexDirection="column" mt={4} gap={1} flexWrap="nowrap" alignItems="center">
                {getPrimaryPhoto(bookableOrderPage) && (
                  <Grid item xs={isSmallScreen ? 12 : 8}>
                    <StyledImage
                      src={getPrimaryPhoto(bookableOrderPage, isSmallScreen ? 500 : 800)}
                      alt="Experience cover"
                      style={{ border: `1px solid ${theme.customTheme.palette.border}` }}
                    />
                  </Grid>
                )}
                <HeadlinePrice />
                <Typography
                  variant={isSmallScreen ? undefined : 'body2'}
                  fontWeight="800"
                  dangerouslySetInnerHTML={{ __html: orderPageState.orderPage.description?.[language] || '' }}
                />
              </Grid>
            )}
            {specificEvent && currentStepIndex === 0 && (
              <SpecificEventSummary specificEvent={orderPageState.specificEvent!} />
            )}
            <StepRenderer formRef={formRef} setShouldDisableNext={setShouldDiasbleNext} />
            <Grid container className="middle-box" mb={2} justifyContent="center">
              {error === OrderPageErrors.DATE_AND_TIME_NOT_SELECTED &&
                (currentStepId === StepId.DETAILS ||
                  currentStepId === StepId.DATE_AND_TIME ||
                  currentStepId === StepId.VOUCHER_SUMMARY) &&
                (!orderPageState.dateAndTimeDetails.date || !orderPageState.dateAndTimeDetails.time) && (
                  <Fade in timeout={1000}>
                    <Alert sx={{ width: '230px', marginTop: '16px' }} severity="error">
                      <OrderPageTypography color="error">{t('order_page.date_error')}</OrderPageTypography>
                    </Alert>
                  </Fade>
                )}
            </Grid>
            <OrderPageActions
              loading={loading}
              shouldDisableNext={shouldDiasbleNext}
              onNext={handleNext}
              onBack={handleBack}
              onResetDialogOpen={() => setIsResetDialogOpen(true)}
              nextButtonText={nextButtonText}
            />
          </Grid>
        </Grid>

        {!storefrontMode && (
          <MonkeybookWidgetFooter languages={orderPageLanguages} accountId={orderPageState.account.id} />
        )}

        <ConfirmationDialog
          title={t('order_page.reset_dialog.title')}
          ctaText={t('reset')}
          isOpen={isResetDialogOpen}
          onClose={(actionConfirmed: any) => {
            setIsResetDialogOpen(false);
            if (actionConfirmed) {
              void handleReset();
            }
          }}
        />
      </Grid>
    </Grid>
  );
};

export default OrderPage;
